/**
 * InfinitSales - CRM embutido em painel de parceiro (white label, 2026-10-22)
 *
 * O painel de um cliente (ex.: Painel Bold) abre este app num iframe, em
 * /embed, e entrega por postMessage uma sessão curta emitida para a chave de
 * API dele (backend: services/embed_session.py). As telas são as mesmas; o
 * modo embutido só tira a barra lateral e aplica a marca do workspace.
 *
 * O estado do modo embutido fica no sessionStorage (por aba e, dentro de um
 * iframe de outro site, separado da sessão normal da InfinitSales).
 *
 * Mantido puro (só storage e window, sem importar api nem React), como
 * utils/delegation.js: o interceptor do api.js e o ThemeProvider leem daqui.
 */
const KEY = 'infinitsales-embed';

export const EMBED_MSG = {
  READY: 'infinitsales:ready',
  SESSION: 'infinitsales:session',
  TOKEN: 'infinitsales:token',
  NAVIGATE: 'infinitsales:navigate',
  ROUTE: 'infinitsales:route',
  EXPIRED: 'infinitsales:session-expired',
};

// Telas que o painel pode abrir. Pedido fora desta lista é ignorado.
export const EMBED_SCREENS = {
  funis: '/funis',
  'wa-business': '/wa-business',
  inbox: '/inbox',
  instagram: '/instagram',
  messenger: '/messenger',
  widget: '/widget',
  campaigns: '/campaigns',
  followups: '/followups',
};

const HEX = /^#[0-9a-fA-F]{6}$/;
const FONTE = /^[A-Za-z0-9 ]{2,40}$/;
const CORES = ['accent', 'accent_hover', 'paper', 'ink'];

function inFrame() {
  try {
    return window.self !== window.top;
  } catch {
    return true; // topo de outra origem: estamos num iframe
  }
}

export function getEmbedState() {
  try {
    const raw = sessionStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function setEmbedState(state) {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(state));
  } catch {
    /* storage indisponível: o modo embutido simplesmente não liga */
  }
}

/** Só vale dentro de um iframe E com uma sessão de painel aberta nesta aba. */
export function isEmbedded() {
  return inFrame() && !!getEmbedState()?.origin;
}

export function getEmbedBranding() {
  return isEmbedded() ? getEmbedState()?.branding || null : null;
}

/** Claims do JWT sem conferir assinatura (quem confere é o backend). */
export function decodeJwtPayload(token) {
  try {
    const parte = String(token || '').split('.')[1];
    if (!parte) return null;
    const base64 = parte.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(base64 + '==='.slice((base64.length + 3) % 4))
        .split('')
        .map((c) => `%${c.charCodeAt(0).toString(16).padStart(2, '0')}`)
        .join(''),
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}

/** Marca que pode chegar ao CSS: cores #rrggbb, fonte simples e logo https. */
export function sanitizeBranding(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const marca = {};
  if (typeof raw.name === 'string' && raw.name.trim()) marca.name = raw.name.trim().slice(0, 60);
  CORES.forEach((c) => {
    if (typeof raw[c] === 'string' && HEX.test(raw[c])) marca[c] = raw[c].toLowerCase();
  });
  if (typeof raw.font === 'string' && FONTE.test(raw.font)) marca.font = raw.font;
  if (typeof raw.logo_url === 'string' && raw.logo_url.startsWith('https://')) marca.logo_url = raw.logo_url;
  return Object.keys(marca).length ? marca : null;
}

function sanitizeOpenChat(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const out = {};
  Object.keys(raw).slice(0, 4).forEach((k) => {
    const v = raw[k];
    if (/^[a-z_]{2,30}$/.test(k) && typeof v === 'string' && v.length <= 200) out[k] = v;
  });
  return Object.keys(out).length ? out : null;
}

/**
 * Mensagem de sessão do painel, validada: veio da janela-mãe, o token é de
 * sessão embutida, a origem gravada no token é a de quem mandou e ele não
 * venceu. Qualquer outra coisa devolve null.
 */
export function sessionFromMessage(event) {
  const m = event && event.data;
  if (!m || typeof m !== 'object') return null;
  if (m.type !== EMBED_MSG.SESSION && m.type !== EMBED_MSG.TOKEN) return null;
  if (event.source && event.source !== window.parent) return null;
  if (typeof m.token !== 'string') return null;
  const payload = decodeJwtPayload(m.token);
  if (!payload || payload.embed !== true || !payload.embed_origin || !payload.user_id) return null;
  if (payload.embed_origin !== event.origin) return null;
  if (payload.exp && payload.exp * 1000 <= Date.now()) return null;
  return {
    type: m.type,
    token: m.token,
    payload,
    origin: event.origin,
    screen: typeof m.screen === 'string' ? m.screen : null,
    branding: sanitizeBranding(m.branding),
    openChat: sanitizeOpenChat(m.openChat),
  };
}

export function routeForScreen(screen) {
  return Object.prototype.hasOwnProperty.call(EMBED_SCREENS, screen) ? EMBED_SCREENS[screen] : null;
}

/** Fala só com o painel dono da sessão (targetOrigin = origem gravada). */
export function postToParent(msg) {
  const st = getEmbedState();
  if (!st?.origin || !inFrame()) return;
  try {
    window.parent.postMessage(msg, st.origin);
  } catch {
    /* janela-mãe fechou */
  }
}

/** Espaço da barra da NavSidebar no celular: some no modo embutido. */
export function navMobileTop() {
  return isEmbedded() ? 0 : 54;
}

export function navMobileBottom() {
  return isEmbedded() ? 0 : 58;
}

/** Carrega a fonte da marca do Google Fonts e aplica no documento. */
export function applyBrandFont(font) {
  if (!font || !FONTE.test(font)) return;
  const id = 'infinitsales-brand-font';
  if (!document.getElementById(id)) {
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${font.trim().replace(/ /g, '+')}:wght@400;500;600&display=swap`;
    document.head.appendChild(link);
  }
  document.body.style.fontFamily = `'${font}', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif`;
}
